/**
 * AssetsInProduction — Sprint 5 · Project Symphony
 *
 * Per-group counts (Social · Blogs · Emails · Videos · …) with each
 * status broken out (Ready · Working · Waiting · Attention). Reads
 * from the same GenerationState polled every 3 seconds.
 */
import { Card } from "@/shared/ui/card";
import type { GenerationState } from "../types";
import { CHANNEL_LABEL } from "@/features/marketing-strategy/types";

const GROUP_LABEL: Record<string, string> = {
  social: "Social",
  emails: "Emails",
  blogs: "Blogs",
  videos: "Videos",
  landing_pages: "Landing Pages",
  ads: "Ads",
  sms: "SMS",
  flyers: "Flyers",
  visuals: "Visuals",
  other: "Other",
};

function humanGroup(key: string): string {
  return GROUP_LABEL[key] ?? CHANNEL_LABEL[key] ?? key;
}

export function AssetsInProduction({
  state,
}: {
  state: GenerationState | undefined;
}): JSX.Element | null {
  const entries = state ? Object.entries(state.groups) : [];
  const isEmpty = entries.length === 0;

  return (
    <section data-testid="room-assets-in-production">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Assets in Production
        </p>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          {isEmpty ? "Nothing in production yet" : "Where each department stands"}
        </h2>
      </div>

      {isEmpty ? (
        // Patch 37.4.9 — never leave the anchor target invisible. When
        // the campaign has no generation groups yet, render a visible
        // empty-state card so tapping the "Assets" jump-nav pill scrolls
        // to something meaningful instead of a 0-height ghost.
        <Card
          className="border-dashed border-border/60 bg-background/40 p-8 text-center"
          data-testid="room-assets-empty"
        >
          <p className="text-sm text-muted-foreground">
            No assets are being generated for this campaign yet.
          </p>
          <p className="mt-1 text-xs text-muted-foreground/80">
            Approve the campaign brief to hand off to the Content Team and
            Creative Studio — asset counts will populate here as
            departments start work.
          </p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {entries.map(([key, counts]) => (
            <Card
              key={key}
              className="p-5"
              data-testid={`room-generation-group-${key}`}
            >
            <div className="flex items-baseline justify-between mb-3">
              <span className="font-semibold">{humanGroup(key)}</span>
              <span className="text-2xl font-semibold tabular-nums">
                {counts.total}
              </span>
            </div>
            <ul className="space-y-1 text-sm">
              <li className="flex items-center justify-between text-emerald-600">
                <span>Ready</span>
                <span
                  className="tabular-nums font-medium"
                  data-testid={`room-generation-ready-${key}`}
                >
                  {counts.review_ready}
                </span>
              </li>
              {counts.generating > 0 && (
                <li className="flex items-center justify-between text-primary">
                  <span>Working</span>
                  <span className="tabular-nums font-medium">
                    {counts.generating}
                  </span>
                </li>
              )}
              {counts.queued > 0 && (
                <li className="flex items-center justify-between text-amber-600">
                  <span>Waiting</span>
                  <span className="tabular-nums font-medium">
                    {counts.queued}
                  </span>
                </li>
              )}
              {counts.attention_required > 0 && (
                <li className="flex items-center justify-between text-rose-600">
                  <span>Needs attention</span>
                  <span className="tabular-nums font-medium">
                    {counts.attention_required}
                  </span>
                </li>
              )}
              {counts.skipped > 0 && (
                <li className="flex items-center justify-between text-muted-foreground">
                  <span>Deferred</span>
                  <span className="tabular-nums font-medium">
                    {counts.skipped}
                  </span>
                </li>
              )}
            </ul>
          </Card>
        ))}
        </div>
      )}
    </section>
  );
}
